
import React, { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import { useSidebar } from "@/context/SidebarContext";
import HeaderLogo from "./HeaderLogo";
import AddLeadLink from "./AddLeadLink";
import SubscriptionBadge from "./SubscriptionBadge";
import ProfileLink from "./ProfileLink";
import UnifiedSidebar from "@/components/navigation/UnifiedSidebar";
import CompanyBanner from "./CompanyBanner";
import CompanySwitcher from "@/components/CompanySwitcher";
import { NotificationCenter } from "@/components/tasks/NotificationCenter";
import { Link } from "react-router-dom";
import { Button, buttonVariants } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Plus } from "lucide-react";
import AddTaskDialog from "@/components/tasks/AddTaskDialog";

const Header = () => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const navContext = useNavigationContext();
  const { leadName } = useSidebar();
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-white/5 bg-[hsl(222,47%,7%)]/95 backdrop-blur-md">
      <div className="flex h-14 items-center justify-between px-2 sm:px-3 md:px-4 lg:px-6 w-full max-w-full gap-2">
        {/* Left section */}
        <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1 overflow-hidden">
          {isMobile && (
            <UnifiedSidebar
              context={navContext.context}
              leadName={leadName}
            />
          )}
          <HeaderLogo />
          {!isMobile && user && <CompanySwitcher variant="ghost" className="max-w-[120px] md:max-w-[160px]" />}
        </div>

        {/* Right section */}
        <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2.5 flex-shrink-0">
          {/* Add Lead Button - Compact on mobile, full on desktop */}
          <AddLeadLink className={isMobile ? "h-8 px-2 gap-1 text-xs" : ""} />
          
          {/* Add Task Button - Desktop/Tablet Only */}
          {!isMobile && (
            <Button
              size="sm"
              className="gap-1 flex-shrink-0 h-8 px-2 sm:px-3"
              onClick={() => setIsTaskDialogOpen(true)}
            >
              <Plus className="h-4 w-4" />
              <span className="hidden md:inline text-xs sm:text-sm">Add Task</span>
            </Button>
          )}
          
          {/* Pricing Link - Hidden on Mobile and for logged in users */}
          {!user && !isMobile && (
            <Link to="/pricing" className={buttonVariants({ variant: "ghost" })}>
              Pricing
            </Link>
          )}
          
          {/* Notification Center - Show on all sizes */}
          <NotificationCenter />
          
          {/* Subscription - Hidden on Mobile */}
          {!isMobile && <SubscriptionBadge />}
          
          {/* Profile Avatar - Only show on Desktop */}
          {!isMobile && <ProfileLink />}
        </div>
      </div>
      
      {/* Active Company Indicator */}
      <CompanyBanner />
      
      <AddTaskDialog
        isOpen={isTaskDialogOpen}
        onClose={() => setIsTaskDialogOpen(false)}
      />
    </header>
  );
};

export default Header;
